import React from 'react';
import { Box, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useSelector, useDispatch } from 'react-redux';
import { findStatusColor, getUrlWithParams, hasCommonElement } from '../../utils/Utils';
import { GENERAL_USER } from '../../utils/Constants';
import { setHistoricalCyclesValue } from '../../redux/General/generalSlice';

const CardComponent = ({ cards, userRole, isCalendarCreated }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();

  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedCycle = newParams.get('cycleName');
  const bu = newParams.get('bu');
  const comprehensiveTaskDataRx = useSelector((state) => state.calendar.comprehensiveData);
  const selectedCycleObj = useSelector((state) => state.cycle.selectedCycleObj);

  const findStatusOfTask = (e2eTask) => {
    if (comprehensiveTaskDataRx) {
      if (typeof e2eTask == 'string') {
        const reqTask = comprehensiveTaskDataRx?.find((item) => item?.name == e2eTask);
        return reqTask?.status;
      }
      if (Array.isArray(e2eTask)) {
        const matchingTasks = comprehensiveTaskDataRx?.filter((item) =>
          e2eTask.includes(item?.name)
        );

        if (matchingTasks?.length === e2eTask?.length) {
          const allNotStarted = matchingTasks.every((task) => task.status === 'Not Started');
          const allCompleted = matchingTasks.every((task) => task.status === 'Completed');

          if (allNotStarted) return 'Not Started';
          if (allCompleted) return 'Completed';
          return 'In Progress';
        }
      }
    }
  };

  const findTaskColor = (e2eTask, status) => {
    if (comprehensiveTaskDataRx) {
      if (typeof e2eTask == 'string') {
        const reqTask = comprehensiveTaskDataRx?.find((item) => item?.name == e2eTask);
        return findStatusColor(status, reqTask);
      }
      if (Array.isArray(e2eTask)) {
        return getStatusColor(status);
      }
    }
  };

  // Function to get background color based on status
  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'rgba(179, 218, 203, 1)'; // Green
      case 'Complete with Delay':
        return '#FFC085';
      case 'In Progress':
        return 'rgba(255, 236, 187, 1)'; // Yellow
      case 'Not Started':
        return 'rgba(223, 223, 223, 1)'; // Gray
      case 'Over Due':
        return '#FF0000'; // Red
      default:
        return '#D3D3D3'; // Default light gray for unknown status
    }
  };

  // Array of card titles for which the status should be hidden
  const cardsWithoutStatus = [
    'Calendar',
    'Portfolio Summary',
    'Portfolio Detail View',
    'Brand & Market Insights',
    'Cycle Action Summary'
  ];

  const isTPOUser = hasCommonElement(userRole, [GENERAL_USER]);
  let disableCards = ['PO Manual Data Upload', 'Cycle Trigger'];
  if (!isCalendarCreated && isTPOUser) {
    disableCards.push('Calendar');
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap', // Allow wrapping of cards
        gap: '8px',
        boxSizing: 'border-box', // Include padding in total width
        width: '100%' // Avoid exceeding the container width
      }} // Added flexWrap and justifyContent
    >
      {cards.map((card, index) => (
        <Stack
          key={`${index + 1}`}
          sx={{
            justifyContent: 'space-between',
            padding: '16px',
            width: '260px',
            height: '190px',
            border: '1px solid #D7E5F8',
            borderRadius: '8px',
            opacity: card.isCardDisabled ? 0.5 : 1, // Reduce opacity to give a "disabled" effect
            backgroundColor: card.isCardDisabled ? '#f2f2f2' : 'white' // Change background color to indicate disabled state
          }}>
          <Stack direction="row" gap="12px">
            <Stack gap={1}>
              <Typography variant="h6">{card.cardHeader}</Typography>
              <Typography variant="extraSmall">{card.cardContent}</Typography>
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
            {/* Render status only for cards that are not in cardsWithoutStatus */}
            {!cardsWithoutStatus.includes(card.cardHeader) &&
              card.showStatus &&
              comprehensiveTaskDataRx?.length > 0 && (
                <Box
                  sx={{
                    width: '100px',
                    height: '16px',
                    background: findTaskColor(card?.e2eTask, findStatusOfTask(card?.e2eTask)),
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  {/* changed to dynamic e2e task grouping status */}
                  <span style={{ fontSize: '12px' }}>{findStatusOfTask(card?.e2eTask)}</span>
                </Box>
              )}
            {/* Align buttons to the right */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              {disableCards.includes(card?.cardHeader) && userRole && isTPOUser ? (
                <Button
                  data-testid={`${card.id}-view-btn-disabled`}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled
                  endIcon={<ArrowForwardIcon />}
                  sx={{ width: '64px !important', height: '24px !important' }}>
                  View
                </Button>
              ) : (
                <Button
                  data-testid={`${card.id}-view-btn`}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={
                    card.disabled || card.id == 'calendar' ? selectedCycleObj['testCycle'] : false
                  }
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    if (card.navigateState) {
                      navigate(
                        getUrlWithParams(card.actionLink, bu, selectedCycle, selectedCycleId),
                        {
                          state: {
                            tabName: card.navigateState
                          }
                        }
                      );
                    } else {
                      dispatch(setHistoricalCyclesValue('cycleTriggerTable'));
                      navigate(
                        getUrlWithParams(card.actionLink, bu, selectedCycle, selectedCycleId)
                      );
                    }
                  }}
                  sx={{ width: '64px !important', height: '24px !important' }}>
                  {card.cardButton}
                </Button>
              )}
            </Box>
          </Stack>
        </Stack>
      ))}
    </Box>
  );
};

export default CardComponent;
